import { useMutation } from "@tanstack/react-query";
import { useId, useState } from "react";
import { useNavigate } from "react-router";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { post } from "@/lib/api";
import { centsEur, convertAmountCents, type ConvertChoice } from "@/lib/documents";
import { date as deDate, parseNumber } from "@/lib/format";
import type { Document, DocumentDetail } from "@/lib/types";
import { cn } from "@/lib/utils";
import { toCents } from "../../../invoicing/money.ts";
import { useRefreshDocument } from "./useDocument";

type Mode = "partial" | "positions" | "final";

const MODES: { id: Mode; label: string }[] = [
  { id: "partial", label: "Anzahlung / Teilbetrag" },
  { id: "positions", label: "Teilrechnung nach Positionen" },
  { id: "final", label: "Schlussrechnung" },
];

/** The invoice draft that „In Teilen verrechnen …“ would create, or why it cannot yet. */
function choiceOf(mode: Mode, unit: "%" | "€", value: string, shares: Record<number, string>): ConvertChoice | string {
  if (mode === "final") return { kind: "final" };
  if (mode === "partial") {
    const n = parseNumber(value);
    if (n === null || !(n > 0)) return unit === "%" ? "Bitte einen Prozentsatz eingeben" : "Bitte einen Betrag eingeben";
    if (unit === "%" && n > 100) return "Höchstens 100 %";
    return unit === "%" ? { kind: "percent", percent: n } : { kind: "amount", amount: n };
  }
  const lines: { positionId: number; share: number }[] = [];
  for (const [id, text] of Object.entries(shares)) {
    if (text.trim() === "") continue;
    const pct = parseNumber(text);
    if (pct === null || pct < 0 || pct > 100) return "Ein Anteil liegt zwischen 0 und 100 %";
    if (pct > 0) lines.push({ positionId: Number(id), share: pct / 100 });
  }
  return lines.length > 0 ? { kind: "positions", lines } : "Bitte bei mindestens einer Position einen Anteil eingeben";
}

/** „In Teilen verrechnen …“ for an accepted offer: Anzahlung or Teilbetrag, a Teilrechnung by positions, or the Schlussrechnung. */
export function ConvertDialog({ detail, onClose }: { detail: DocumentDetail; onClose: () => void }) {
  const offer = detail.document;
  const billing = detail.billing!;
  const navigate = useNavigate();
  const refresh = useRefreshDocument(offer.id);
  const ids = useId();
  const [mode, setMode] = useState<Mode>("partial");
  const [unit, setUnit] = useState<"%" | "€">("%");
  const [value, setValue] = useState("");
  const [shares, setShares] = useState<Record<number, string>>({});
  const choice = choiceOf(mode, unit, value, shares);
  const amount = typeof choice === "string" ? null : convertAmountCents(offer, billing.billedCents, choice);
  const earlier = billing.invoices.filter((i) => i.number !== null && i.status !== "cancelled");

  const create = useMutation({
    mutationFn: (c: ConvertChoice) => post<Document>(`/api/documents/${offer.id}/convert`, c),
    onSuccess: (draft) => {
      onClose();
      navigate(`/rechnungen/${draft.id}`);
    },
    onSettled: refresh,
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[calc(100dvh-2rem)] gap-0 overflow-y-auto p-0 sm:max-w-xl">
        <DialogHeader className="px-6 pt-5.5 pb-4 text-left">
          <DialogTitle>In Teilen verrechnen</DialogTitle>
          <DialogDescription>{offer.number} · {centsEur(offer.totalCents)}</DialogDescription>
        </DialogHeader>
        <fieldset className="flex flex-col gap-3 px-6 pb-5">
          <legend className="sr-only">Wie verrechnen?</legend>
          {MODES.map((m) => (
            <div key={m.id} className={cn("flex flex-col gap-3 rounded-lg border p-3", mode === m.id && "border-foreground/40")}>
              <label className="flex items-center gap-2.5 text-sm font-medium pointer-coarse:min-h-11">
                <input
                  type="radio"
                  name={`${ids}-mode`}
                  value={m.id}
                  checked={mode === m.id}
                  onChange={() => setMode(m.id)}
                  className="size-4 accent-primary"
                />
                {m.label}
              </label>
              {mode === m.id && m.id === "partial" && (
                <div className="flex flex-wrap items-center gap-2 pl-6.5">
                  <Input
                    aria-label={unit === "%" ? "Prozent des Angebots" : "Betrag in Euro"}
                    inputMode="decimal"
                    autoFocus
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    className="w-32 text-right tabular-nums pointer-coarse:h-11"
                  />
                  <div role="group" aria-label="Einheit" className="flex gap-1">
                    {(["%", "€"] as const).map((u) => (
                      <button
                        key={u}
                        type="button"
                        aria-pressed={unit === u}
                        onClick={() => setUnit(u)}
                        className={cn(
                          "h-9 min-w-9 rounded-md border px-2.5 text-sm outline-none transition-colors duration-150 focus-visible:ring-[3px] focus-visible:ring-ring/50 motion-reduce:transition-none pointer-coarse:h-11 pointer-coarse:min-w-11",
                          unit === u ? "border-primary bg-primary text-primary-foreground" : "bg-card text-muted-foreground hover:text-foreground",
                        )}
                      >
                        {u}
                      </button>
                    ))}
                  </div>
                  <span aria-live="polite" className="text-[0.8125rem] text-muted-foreground tabular-nums">
                    {value.trim() !== "" && parseNumber(value) !== null ? (unit === "€" ? `= ${centsEur(toCents(parseNumber(value)!))}` : `= ${String(parseNumber(value)).replace(".", ",")} %`) : ""}
                  </span>
                </div>
              )}
              {mode === m.id && m.id === "positions" && (
                <ul className="flex flex-col divide-y pl-6.5">
                  {offer.positions.map((p) => (
                    <li key={p.id} className="flex items-center gap-3 py-2">
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm">{p.title}</span>
                        <span className="text-xs text-muted-foreground tabular-nums">{centsEur(p.totalCents)}</span>
                      </span>
                      <div className="relative">
                        <Input
                          aria-label={`Anteil von ${p.title} in Prozent`}
                          inputMode="decimal"
                          placeholder="0"
                          value={shares[p.id] ?? ""}
                          onChange={(e) => setShares((s) => ({ ...s, [p.id]: e.target.value }))}
                          className="w-24 pr-7 text-right tabular-nums pointer-coarse:h-11"
                        />
                        <span aria-hidden className="pointer-events-none absolute inset-y-0 right-2.5 flex items-center text-sm text-muted-foreground">%</span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
              {mode === m.id && m.id === "final" && (
                earlier.length === 0 ? (
                  <p className="pl-6.5 text-[0.8125rem] text-muted-foreground">Noch keine Teilrechnungen – die Schlussrechnung verrechnet das ganze Angebot.</p>
                ) : (
                  <ul className="flex flex-col gap-1 pl-6.5 text-[0.8125rem]">
                    {earlier.map((i) => (
                      <li key={i.id} className="flex gap-3 tabular-nums">
                        <span className="w-20 shrink-0 font-medium">{i.number}</span>
                        <span className="w-22 shrink-0 text-muted-foreground">{i.date ? deDate(i.date) : ""}</span>
                        <span className="ml-auto">−{centsEur(i.totalCents)}</span>
                      </li>
                    ))}
                  </ul>
                )
              )}
            </div>
          ))}
        </fieldset>
        <div className="sticky bottom-0 flex flex-col gap-3 border-t bg-background px-6 py-3.5">
          {create.isError && <p role="alert" className="text-[0.8125rem] text-destructive">{create.error.message}</p>}
          <DialogFooter className="flex-row flex-wrap items-center gap-x-4 gap-y-2 sm:justify-start">
            <span className="flex min-w-0 flex-col text-[0.8125rem]">
              <span className="text-muted-foreground">{billing.label}</span>
              <span aria-live="polite" className={cn("tabular-nums", typeof choice === "string" && "text-muted-foreground")}>
                {amount !== null ? <>Diese Rechnung: <span className="font-semibold">{centsEur(amount)}</span></> : typeof choice === "string" && (value || Object.keys(shares).length > 0) ? choice : " "}
              </span>
            </span>
            <Button variant="outline" className="ml-auto pointer-coarse:h-11" onClick={onClose}>Abbrechen</Button>
            <Button
              className="pointer-coarse:h-11"
              disabled={typeof choice === "string" || create.isPending}
              onClick={() => typeof choice !== "string" && create.mutate(choice)}
            >
              Entwurf anlegen
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
